import { getApiEndpoint } from '@/config/api';

export function authHeaders(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  const token = window.localStorage.getItem('authToken');
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) h['Authorization'] = `Bearer ${token}`;
  return h;
}

/** Só `Authorization` (ex.: `FormData` — não definir `Content-Type`). */
export function authBearerOnly(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  const token = window.localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export class ApiRequestError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

function messageFromErrorPayload(data: unknown, fallback: string): string {
  if (!data || typeof data !== 'object') return fallback;
  const o = data as Record<string, unknown>;
  const err = o.error;
  const msg = o.message;
  const details = o.details;
  if (typeof err === 'string' && err.trim()) return err.trim();
  if (typeof msg === 'string' && msg.trim()) return msg.trim();
  if (typeof details === 'string' && details.trim()) return details.trim();
  return fallback;
}

const ARRAY_PAYLOAD_KEYS = [
  'data',
  'items',
  'results',
  'rows',
  'records',
  'bookings',
  'conversations',
  'notifications',
  'properties',
  'photos',
  'messages',
  'periods',
  'availability',
  'favorites',
  'favoritos',
  'users',
  'developments',
  'development',
  'partners',
  'vouchers',
] as const;

/** Garante lista a partir de resposta da API (array direto ou objeto com chave de lista). */
export function normalizeApiArray<T>(data: unknown): T[] {
  if (Array.isArray(data)) {
    return data as T[];
  }
  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>;
    for (const key of ARRAY_PAYLOAD_KEYS) {
      const value = record[key];
      if (Array.isArray(value)) {
        return value as T[];
      }
    }
  }
  return [];
}

export async function apiJsonArray<T>(
  path: string,
  init: RequestInit = {},
): Promise<T[]> {
  const data = await apiJson<unknown>(path, init);
  return normalizeApiArray<T>(data);
}

/** Upload multipart (não define Content-Type — o browser envia o boundary). */
export async function apiFormUpload<T>(
  path: string,
  formData: FormData,
  init: Omit<RequestInit, 'body'> = {},
): Promise<T> {
  const url = path.startsWith('http') ? path : getApiEndpoint(path);
  const res = await fetch(url, {
    ...init,
    method: init.method ?? 'POST',
    headers: {
      ...authBearerOnly(),
      ...(init.headers as Record<string, string>),
    },
    body: formData,
  });
  const text = await res.text();
  let data: unknown;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (!res.ok) {
    const fallback = text || res.statusText || `HTTP ${res.status}`;
    const msg = messageFromErrorPayload(data, fallback);
    throw new ApiRequestError(
      typeof msg === 'string' ? msg : `HTTP ${res.status}`,
      res.status,
      data,
    );
  }
  return data as T;
}

export async function apiJson<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const url = path.startsWith('http') ? path : getApiEndpoint(path);
  const res = await fetch(url, {
    ...init,
    headers: {
      ...authHeaders(),
      ...(init.headers as Record<string, string>),
    },
  });
  const text = await res.text();
  let data: unknown;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (!res.ok) {
    const fallback = text || res.statusText || `HTTP ${res.status}`;
    const msg = messageFromErrorPayload(data, fallback);
    throw new ApiRequestError(
      typeof msg === 'string' ? msg : `HTTP ${res.status}`,
      res.status,
      data,
    );
  }
  return data as T;
}
